import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

export type PackingServicesPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
};

function createPackingServicesPage(config: PackingServicesPageConfig): ComponentType {
  return function PackingServicesPage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        serviceMenuPath="/services/packing-services"
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
      />
    );
  };
}

const baseHighlights = (location: string): string[] => [
  `Full and partial packing services across ${location}`,
  'High-quality, protective packing materials included as standard',
  'Trained packers who handle fragile, valuable, and awkward items with care',
  'Clear room-by-room labelling and inventory for a smooth unpack',
];

const baseProcess = (location: string): string[] => [
  `We visit or consult with you in ${location} to understand your home, items, and timeline.`,
  'A tailored packing plan is prepared covering materials, schedule, and special-item handling.',
  'Our packers arrive on the agreed date, pack methodically, and protect every item properly.',
  'Boxes are clearly labelled and, on request, we unpack at your new home so you can settle in fast.',
];

const baseIdealFor = (location: string): string[] => [
  `Busy homeowners and families in ${location} who want a stress-free move`,
  'Professionals, landlords, and tenants needing a quick, reliable packing turnaround',
  'Anyone with fragile, high-value, or sentimental items requiring expert protection',
];

export const packingServicesPageConfigs: PackingServicesPageConfig[] = [
  {
    path: '/services/packing-services/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Packing Services in Staffordshire',
    title: 'Packing Services in Staffordshire',
    description: 'Professional full and partial packing services across Staffordshire from VIP House Movers, with premium materials and trained packers.',
    intro: 'Moving in Staffordshire is easier when the packing is handled by experts. Our team takes care of the boxes, bubble wrap, and fragile items so you can focus on the rest of your move with complete peace of mind.',
    highlights: baseHighlights('Staffordshire'),
    process: baseProcess('Staffordshire'),
    idealFor: baseIdealFor('Staffordshire'),
  },
  {
    path: '/services/packing-services/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Packing Services in Wolverhampton',
    title: 'Packing Services in Wolverhampton',
    description: 'VIP House Movers delivers premium packing services in Wolverhampton, using high-grade materials and a meticulous, room-by-room approach.',
    intro: 'As a Wolverhampton-based removals company, we know exactly how to pack homes and flats in the city quickly, safely, and with the level of care VIP clients expect.',
    highlights: baseHighlights('Wolverhampton'),
    process: baseProcess('Wolverhampton'),
    idealFor: baseIdealFor('Wolverhampton'),
  },
  {
    path: '/services/packing-services/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Packing Services in Birmingham',
    title: 'Packing Services in Birmingham',
    description: 'Trusted packing services in Birmingham by VIP House Movers, ideal for houses, apartments, and high-value items across the city.',
    intro: 'Birmingham moves often mean tight schedules and complex buildings. Our packing crew plans every job around access, parking, and timing so your belongings are packed, protected, and ready on move day.',
    highlights: baseHighlights('Birmingham'),
    process: baseProcess('Birmingham'),
    idealFor: baseIdealFor('Birmingham'),
  },
  {
    path: '/services/packing-services/walsall',
    navLabel: 'Walsall',
    cardTitle: 'Packing Services in Walsall',
    title: 'Packing Services in Walsall',
    description: 'Reliable packing services in Walsall from VIP House Movers, designed to protect your belongings and save you hours of stressful preparation.',
    intro: 'From full-home packs to help with just the kitchen or fragile items, our Walsall packing service gives you flexible, professional support tailored to your move.',
    highlights: baseHighlights('Walsall'),
    process: baseProcess('Walsall'),
    idealFor: baseIdealFor('Walsall'),
  },
  {
    path: '/services/packing-services/cannock',
    navLabel: 'Cannock',
    cardTitle: 'Packing Services in Cannock',
    title: 'Packing Services in Cannock',
    description: 'Premium packing services in Cannock by VIP House Movers, combining quality materials, trained packers, and a stress-free experience.',
    intro: 'We help Cannock homeowners, tenants, and businesses pack efficiently and safely, using professional techniques so every item arrives at its destination in the same condition it left.',
    highlights: baseHighlights('Cannock'),
    process: baseProcess('Cannock'),
    idealFor: baseIdealFor('Cannock'),
  },
  {
    path: '/services/packing-services/dudley',
    navLabel: 'Dudley',
    cardTitle: 'Packing Services in Dudley',
    title: 'Packing Services in Dudley',
    description: 'Professional packing services in Dudley from VIP House Movers, protecting your valuables with premium materials and careful handling.',
    intro: 'Whether you are moving a small flat or a large family home in Dudley, our packing service gives you the right mix of speed, protection, and organisation for a smoother move day.',
    highlights: baseHighlights('Dudley'),
    process: baseProcess('Dudley'),
    idealFor: baseIdealFor('Dudley'),
  },
  {
    path: '/services/packing-services/west-bromwich',
    navLabel: 'West Bromwich',
    cardTitle: 'Packing Services in West Bromwich',
    title: 'Packing Services in West Bromwich',
    description: 'Expert packing services in West Bromwich by VIP House Movers, designed to save you time and protect every item from bumps and breaks.',
    intro: 'Our West Bromwich packing team treats every home as their own, using quality boxes, wrapping, and protective materials so your possessions travel safely to your new address.',
    highlights: baseHighlights('West Bromwich'),
    process: baseProcess('West Bromwich'),
    idealFor: baseIdealFor('West Bromwich'),
  },
  {
    path: '/services/packing-services/telford',
    navLabel: 'Telford',
    cardTitle: 'Packing Services in Telford',
    title: 'Packing Services in Telford',
    description: 'VIP House Movers provides premium packing services in Telford, with flexible full or partial packs and trained, insured packers.',
    intro: 'From apartments to large family homes across Telford, we handle the packing with precision and care so you can spend less time with bubble wrap and more time planning your next chapter.',
    highlights: baseHighlights('Telford'),
    process: baseProcess('Telford'),
    idealFor: baseIdealFor('Telford'),
  },
];

export const packingServicesPages = packingServicesPageConfigs.map((config) => ({
  ...config,
  component: createPackingServicesPage(config),
}));
